import { ComponentProps, MouseEvent, Ref, Suspense, forwardRef } from "react";
import { FormattedMessage } from "react-intl";
import AC, {
  AutoCompleteT,
  useAutoCompleteInputValue,
} from "@/components/AutoComplete";
import { AuthorSchema, AuthorSchemaInput } from "./schema";
import { Author } from "./author";
import { graphql, useLazyLoadQuery } from "react-relay";
import { inputDatasetVersionAuthorQuery } from "./__generated__/inputDatasetVersionAuthorQuery.graphql";
import { useDebounced } from "@/utils/hooks";
import { Button } from "@/kit/ui/button";

const AutoComplete: AutoCompleteT<AuthorSchemaInput> = AC;

export interface DatasetVersionAuthorInputProps extends Omit<
  ComponentProps<"input">,
  "value" | "onChange"
> {
  value: AuthorSchemaInput[] | undefined | null;
  onChange: (value: AuthorSchemaInput[]) => void;
}

export default forwardRef(function DatasetVersionAuthorInput(
  { value, onChange, ...inputProps }: DatasetVersionAuthorInputProps,
  ref: Ref<HTMLInputElement>,
) {
  const onAddAuthor = (author: AuthorSchemaInput) => {
    if (value?.find((x) => AuthorSchema.eq(x, author))) return;
    const next = value ? value.slice() : [];
    next.push(author);
    onChange(next);
  };

  const onRemoveAuthorAt = (index: number) => {
    if (!value?.length) return;
    const next = value.slice();
    if (index < 0) {
      index = next.length + index;
    }
    next.splice(index, 1);
    onChange(next);
  };

  const onAddUnregisteredAuthor = (fullName: string) => {
    const author: AuthorSchemaInput = {
      unregistered: { fullName },
    };
    if (value?.find((x) => AuthorSchema.eq(x, author))) return;
    const next = value ? value.slice() : [];
    next.push(author);
    onChange(next);
  };

  return (
    <AutoComplete.Root
      onSelectItem={onAddAuthor}
      onPopItem={() => onRemoveAuthorAt(-1)}
    >
      <AutoComplete.Input
        ref={ref}
        className="flex flex-wrap gap-2"
        onSubmitItem={onAddUnregisteredAuthor}
        {...inputProps}
      >
        {value?.map((author, index) => (
          <Author
            key={authorKey(author, index)}
            author={author}
            onRemove={onRemoveAuthorAt.bind(null, index)}
          />
        ))}
      </AutoComplete.Input>

      <AutoComplete.Portal className="aria-busy:pointer-events-none aria-busy:text-grey-600">
        <Suspense
          fallback={
            <span className="text-sm">
              <FormattedMessage defaultMessage="Please wait..." />
            </span>
          }
        >
          <AuthorList onAddAuthor={onAddAuthor} />
        </Suspense>
      </AutoComplete.Portal>
    </AutoComplete.Root>
  );
});

const Query = graphql`
  query inputDatasetVersionAuthorQuery($search: String) {
    entities(first: 6, search: $search) {
      nodes {
        id
        username
        displayName
        ...EntityProfilePicFragment @arguments(thumbnail: true)
      }
    }
  }
`;

interface AuthorListProps {
  onAddAuthor(author: AuthorSchemaInput, evt: MouseEvent): void;
}

function AuthorList({ onAddAuthor }: AuthorListProps) {
  const search = useAutoCompleteInputValue();
  const search_ = useDebounced(search, 800);
  const { entities } = useLazyLoadQuery<inputDatasetVersionAuthorQuery>(Query, {
    search: search_,
  });
  if (!entities.nodes.length) {
    return (
      <span className="text-sm">
        <FormattedMessage
          defaultMessage={`Add author "{author}"`}
          values={{ author: search }}
        />
      </span>
    );
  }
  return (
    <AutoComplete.List>
      {entities.nodes.map((node) => {
        const author = {
          registered: { id: node.id, displayName: node.displayName },
        };
        return (
          <AutoComplete.ListItem key={node.id} value={author}>
            <span className="flex-1">{node.displayName}</span>
            <Button
              variant="tertiary"
              size="xs"
              onClick={onAddAuthor.bind(null, author)}
            >
              <FormattedMessage defaultMessage="Add" />
            </Button>
          </AutoComplete.ListItem>
        );
      })}
    </AutoComplete.List>
  );
}

function authorKey(author: AuthorSchemaInput, index: number) {
  if ("registered" in author) {
    return `registered-${author.registered.id}`;
  } else {
    return `unregistered-${index}`;
  }
}
